/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { clsx } from '@accelint/design-foundation/lib/utils';
import styles from './styles.module.css';
import type { DrawerLayoutProps } from './types';

/**
 * DrawerLayout - CSS Grid layout container for positioning panels
 *
 * A shared layout primitive used by Drawer, Sidenav, and other panel components.
 * Controls how panels are positioned relative to the main content area.
 *
 * @param props - {@link DrawerLayoutProps}
 * @param props.className - Optional CSS class name.
 * @param props.extend - Which drawers should extend to full container dimensions.
 * @param props.push - Which drawers push the main content aside.
 * @returns The rendered DrawerLayout component.
 *
 * @example
 * ```tsx
 * <DrawerLayout extend="left right" push="left">
 *   <DrawerLayoutMain>
 *     // Main content here
 *   </DrawerLayoutMain>
 *   <Drawer placement="left">...</Drawer>
 * </DrawerLayout>
 * ```
 */
export function DrawerLayout({
  className,
  extend = 'left right',
  push,
  ...rest
}: DrawerLayoutProps) {
  return (
    <div
      {...rest}
      className={clsx('group/layout', styles.layout, className)}
      data-extend={extend}
      data-push={push || null}
    />
  );
}
